import React, { useState } from 'react';
import { supabase } from '../supabase';
import { moderateContent, sanitizeText, extractMentions, TAGS } from '../utils/moderation';

const MAX_CHARS = 500;

const PostComposer = ({ feedUser, onPostCreated, spaceId = null, spaceName = null }) => {
  const [content,    setContent]    = useState('');
  const [tag,        setTag]        = useState('Discussion');
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');
  const [warn,       setWarn]       = useState('');
  const [expanded,   setExpanded]   = useState(false);

  const charsLeft   = MAX_CHARS - content.length;
  const isOverLimit = charsLeft < 0;

  const handleContentChange = (e) => {
    const val = e.target.value;
    setContent(val);
    setError('');
    const mod = moderateContent(val);
    if (mod.level === 'warn') setWarn(mod.reason);
    else setWarn('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    if (isOverLimit) return setError(`Post is ${Math.abs(charsLeft)} characters over the limit.`);
    if (!feedUser?.id) return setError('You must be logged in to post.');

    const sanitized = sanitizeText(content);
    const mod = moderateContent(sanitized);
    if (!mod.safe) { setError(mod.reason); return; }

    setSubmitting(true);
    setError('');

    try {
      // For space posts — check if space is in moderated mode
      let isApproved = true;
      if (spaceId) {
        const { data: spaceData } = await supabase
          .from('spaces')
          .select('moderated_mode')
          .eq('id', spaceId)
          .single();
        if (spaceData?.moderated_mode) isApproved = false;
      }

      const { data, error: supaError } = await supabase
  .rpc('create_post', {
    p_author_id:       feedUser.id,
    p_author_username: feedUser.username,
    p_content:         sanitized,
    p_tag:             tag,
    p_space_id:        spaceId || null,
    p_is_approved:     isApproved,
  })
  .single();

      if (supaError) {
        console.error('Post insert error:', supaError);
        throw supaError;
      }

      // Increment post count atomically
      await supabase.rpc('increment_post_count', { p_user_id: feedUser.id });

      // Handle @mentions
      const mentions = extractMentions(sanitized);
      if (mentions.length > 0) {
        const { data: mentionedUsers } = await supabase
          .from('feed_users')
          .select('id, username')
          .in('username', mentions);

        if (mentionedUsers?.length > 0) {
          const notifs = mentionedUsers
            .filter(u => u.id !== feedUser.id)
            .map(u => ({
              recipient_id:   u.id,
              type:           'mention',
              actor_username: feedUser.username,
              post_id:        data.id,
              message:        `${feedUser.username} mentioned you in a post`,
            }));
          if (notifs.length > 0) await supabase.from('notifications').insert(notifs);
        }
      }

      setContent('');
      setTag('Discussion');
      setWarn('');
      setExpanded(false);

      if (!isApproved) {
        setError('');
        alert('Your post is pending moderator approval.');
      } else {
        onPostCreated(data);
      }
    } catch (err) {
      if (err.code === '42501') {
        setError('Permission denied. Please try refreshing the page and logging in again.');
      } else {
        setError('Failed to post. Please try again.');
      }
      console.error('PostComposer error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      {spaceName && (
        <div style={{
          fontSize: '0.75rem', fontWeight: 700,
          color: 'var(--primary-color)', background: 'var(--primary-light)',
          padding: '6px 12px', borderRadius: 'var(--radius-sm)',
          marginBottom: 12, display: 'inline-flex', alignItems: 'center', gap: 6,
        }}>
          🏡 Posting in <span style={{ textDecoration: 'underline' }}>{spaceName}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'var(--primary-light)', color: 'var(--primary-color)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: '0.9rem', flexShrink: 0,
            border: '2px solid var(--primary-color)',
          }}>
            {feedUser?.username?.[0]?.toUpperCase()}
          </div>

          <div style={{ flex: 1 }}>
            {!expanded ? (
              <div
                onClick={() => setExpanded(true)}
                style={{
                  padding: '10px 16px', border: '1.5px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)', cursor: 'text',
                  color: 'var(--text-muted)', background: 'var(--input-bg)',
                  fontSize: '0.95rem', transition: 'all 0.15s ease',
                }}
              >
                {spaceName
                  ? `Share something in ${spaceName}…`
                  : `What's on your mind, ${feedUser?.username}?`}
              </div>
            ) : (
              <>
                <textarea
                  autoFocus
                  value={content}
                  onChange={handleContentChange}
                  placeholder={
                    spaceName
                      ? `Share something in ${spaceName}… (max ${MAX_CHARS} chars)\n\nTip: Use @username to mention someone`
                      : `Share a thought, question, or story… (max ${MAX_CHARS} chars)\n\nTip: Use @username to mention someone`
                  }
                  rows={4}
                  style={{
                    width: '100%', resize: 'none',
                    background: 'var(--input-bg)', color: 'var(--text-color)',
                    borderColor: isOverLimit ? '#dc2626' : undefined,
                    fontFamily: 'DM Sans, sans-serif', lineHeight: 1.6,
                  }}
                />

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  {TAGS.map(t => (
                    <button
                      key={t.label} type="button"
                      onClick={() => setTag(t.label)}
                      style={{
                        padding: '4px 12px', borderRadius: 'var(--radius-full)',
                        border: tag === t.label ? `1.5px solid ${t.color}` : '1.5px solid var(--border-color)',
                        background: tag === t.label ? `${t.color}18` : 'transparent',
                        color: tag === t.label ? t.color : 'var(--text-muted)',
                        fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                        transition: 'all 0.15s ease', fontFamily: 'inherit',
                      }}
                    >
                      {t.emoji} {t.label}
                    </button>
                  ))}
                </div>

                {warn && (
                  <p style={{ color: 'var(--accent-color)', fontSize: '0.82rem', marginTop: 8 }}>
                    ⚠️ {warn}
                  </p>
                )}
                {error && (
                  <p style={{ color: '#dc2626', fontSize: '0.82rem', marginTop: 8 }}>
                    ❌ {error}
                  </p>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                  <span style={{
                    fontSize: '0.8rem', fontWeight: 600,
                    color: isOverLimit ? '#dc2626' : charsLeft <= 50 ? 'var(--accent-color)' : 'var(--text-muted)',
                  }}>
                    {isOverLimit ? `${Math.abs(charsLeft)} over limit` : `${charsLeft} left`}
                  </span>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => { setExpanded(false); setContent(''); setError(''); setWarn(''); }}
                      className="btn secondary-btn"
                      style={{ padding: '7px 16px', fontSize: '0.85rem' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || !content.trim() || isOverLimit}
                      className="btn primary-btn"
                      style={{
                        padding: '7px 20px', fontSize: '0.85rem',
                        opacity: (submitting || !content.trim() || isOverLimit) ? 0.6 : 1,
                      }}
                    >
                      {submitting ? 'Posting…' : 'Post'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default PostComposer;